import React from 'react';
import { FaTimes } from 'react-icons/fa';

/**
 * The audit page's filter bar.
 *
 * ⚠️ TWO TIERS, AND THE DIFFERENCE MATTERS. Scope, date range and `include` are
 * SERVER-SIDE - they change the query and therefore what is loaded. Everything
 * else narrows the window already in memory. Changing a server-side filter
 * triggers a refetch; changing a client-side one never does.
 *
 * ⚠️ THE LISTS ARE BUILT FROM THE LOADED WINDOW, so they only ever offer values
 * that exist in it. An action dropdown listing every action the codebase can
 * write would invite a reader to select one, get nothing back, and conclude it
 * never happened - when the truth is that it is not in this window.
 */

const DATE_PRESETS = [
    { key: '24h', label: 'Last 24 hours', days: 1 },
    { key: '7d', label: 'Last 7 days', days: 7 },
    { key: '30d', label: 'Last 30 days', days: 30 },
    { key: 'custom', label: 'Custom range', days: null }
];

/**
 * The scopes that are not a client project (D17, TWK-25).
 *
 * ⚠️ THREE OF THESE HAVE BEEN WRITTEN FOR MONTHS AND WERE UNREADABLE HERE. The
 * picker offered `_system` and the won clients only, so every row the client
 * wiki and the Script Repository have ever written sat in a partition nothing
 * could select. TME Wiki adds a fourth, and Bill chose to fix the other two in
 * the same change.
 *
 * ⚠️ A `_`-PREFIXED SCOPE HAS NO PROJECT BEHIND IT, and the server knows that:
 * `auditContextFor` short-circuits on the prefix rather than on `_system`
 * alone, or six project reads run against an id that is not one.
 *
 * Exported so the test names the same four the component renders, rather than
 * a copy of them that can drift.
 */
export const SYSTEM_SCOPES = [
    { key: '_system', label: 'System (logins, leads, refusals)' },
    { key: '_tmewiki', label: 'TME Wiki' },
    { key: '_wiki', label: 'Client Wiki' },
    { key: '_scripts', label: 'Script Repository' }
];

export const ROW_TYPES = [
    { key: 'all', label: 'Everything' },
    { key: 'change', label: 'Changes only' },
    { key: 'access', label: 'Access only' },
    { key: 'denied', label: 'Denied attempts' }
];

const selectClass = 'border border-gray-300 rounded px-2 py-1 text-sm bg-white';
const labelClass = 'block text-xs text-gray-500 mb-1';

function AuditFilters({ filters, setFilters, clients, options, origins, disabled }) {
    const set = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));

    return (
        <div className="flex flex-wrap gap-3 items-end px-4 py-3 bg-white border-b border-gray-200">
            <div>
                <label className={labelClass} htmlFor="audit-scope">Scope</label>
                <select
                    id="audit-scope"
                    className={selectClass}
                    value={filters.scope}
                    onChange={e => set('scope', e.target.value)}
                    disabled={disabled}
                >
                    {/* The fixed scopes first, System among them: it is where
                        logins, failed logins, lead activity and denied attempts
                        live, and it is the default. The client projects follow. */}
                    {SYSTEM_SCOPES.map(scope => (
                        <option key={scope.key} value={scope.key}>{scope.label}</option>
                    ))}
                    {clients.map(client => (
                        <option key={client.projectId} value={client.projectId}>
                            {client.company || client.name || client.projectId}
                        </option>
                    ))}
                </select>
            </div>

            <div>
                <label className={labelClass} htmlFor="audit-range">Period</label>
                <select
                    id="audit-range"
                    className={selectClass}
                    value={filters.preset}
                    onChange={e => set('preset', e.target.value)}
                    disabled={disabled}
                >
                    {DATE_PRESETS.map(preset => (
                        <option key={preset.key} value={preset.key}>{preset.label}</option>
                    ))}
                </select>
            </div>

            {filters.preset === 'custom' && (
                <>
                    <div>
                        <label className={labelClass} htmlFor="audit-from">From</label>
                        <input
                            id="audit-from"
                            type="date"
                            className={selectClass}
                            value={filters.startDate}
                            onChange={e => set('startDate', e.target.value)}
                            disabled={disabled}
                        />
                    </div>
                    <div>
                        <label className={labelClass} htmlFor="audit-to">To</label>
                        <input
                            id="audit-to"
                            type="date"
                            className={selectClass}
                            value={filters.endDate}
                            onChange={e => set('endDate', e.target.value)}
                            disabled={disabled}
                        />
                    </div>
                </>
            )}

            <div>
                <label className={labelClass} htmlFor="audit-type">Rows</label>
                <select
                    id="audit-type"
                    className={selectClass}
                    value={filters.rowType}
                    onChange={e => set('rowType', e.target.value)}
                >
                    {ROW_TYPES.map(type => (
                        <option key={type.key} value={type.key}>{type.label}</option>
                    ))}
                </select>
            </div>

            <div>
                <label className={labelClass} htmlFor="audit-action">Action</label>
                <select
                    id="audit-action"
                    className={selectClass}
                    value={filters.action}
                    onChange={e => set('action', e.target.value)}
                >
                    <option value="">Any action</option>
                    {options.actions.map(action => (
                        <option key={action} value={action}>{action}</option>
                    ))}
                </select>
            </div>

            <div>
                <label className={labelClass} htmlFor="audit-entity-type">Entity</label>
                <select
                    id="audit-entity-type"
                    className={selectClass}
                    value={filters.entityType}
                    onChange={e => set('entityType', e.target.value)}
                >
                    <option value="">Any entity</option>
                    {options.entityTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                    ))}
                </select>
            </div>

            <div>
                <label className={labelClass} htmlFor="audit-actor">Who</label>
                <select
                    id="audit-actor"
                    className={selectClass}
                    value={filters.actor}
                    onChange={e => set('actor', e.target.value)}
                >
                    <option value="">Anyone</option>
                    {options.actors.map(actor => (
                        <option key={actor} value={actor}>{actor}</option>
                    ))}
                </select>
            </div>

            <div>
                <label className={labelClass} htmlFor="audit-origin">Origin</label>
                <select
                    id="audit-origin"
                    className={selectClass}
                    value={filters.via}
                    onChange={e => set('via', e.target.value)}
                >
                    <option value="">Anywhere</option>
                    {/* ⚠️ ALL FOUR, ALWAYS - unlike every other list here.
                        `via` is a closed set, so a zero is an ANSWER: "no admin
                        connector row in this window" and "this dropdown does not
                        mention the admin connector" are different statements,
                        and only the first tells you whether an agent touched
                        this project. The absent ones are disabled so selecting
                        one cannot return an empty table. */}
                    {Object.entries(origins).map(([origin, count]) => (
                        <option key={origin} value={origin} disabled={count === 0}>
                            {origin} ({count})
                        </option>
                    ))}
                </select>
            </div>

            <div>
                <label className={labelClass} htmlFor="audit-field">Field</label>
                <select
                    id="audit-field"
                    className={selectClass}
                    value={filters.field}
                    onChange={e => set('field', e.target.value)}
                >
                    <option value="">Any field</option>
                    {options.fields.map(field => (
                        <option key={field} value={field}>{field}</option>
                    ))}
                </select>
            </div>

            <div className="flex-1 min-w-[16rem]">
                <label className={labelClass} htmlFor="audit-search">Search</label>
                {/* The clear button is explicit rather than relying on the
                    native type="search" cross: Firefox does not render one, and
                    a search term that survives a reload with no visible way to
                    drop it makes a full trail look like an empty one. */}
                <div className="relative">
                    <input
                        id="audit-search"
                        type="search"
                        className={`${selectClass} w-full ${filters.search ? 'pr-8' : ''}`}
                        placeholder="Any word in any field of the loaded window"
                        value={filters.search}
                        onChange={e => set('search', e.target.value)}
                    />
                    {filters.search && (
                        <button
                            type="button"
                            onClick={() => set('search', '')}
                            aria-label="Clear search"
                            title="Clear search"
                            className="absolute inset-y-0 right-2 flex items-center text-gray-400 hover:text-gray-700"
                        >
                            <FaTimes className="w-3 h-3" />
                        </button>
                    )}
                </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-600 pb-1">
                <input
                    type="checkbox"
                    checked={filters.hideNoOp}
                    onChange={e => set('hideNoOp', e.target.checked)}
                />
                {/* Set aside, never dropped from the record itself. */}
                Hide no-op rows
            </label>
        </div>
    );
}

export { DATE_PRESETS };
export default AuditFilters;
